/**
 * AddToItinerarySheet — one door for everything a traveler adds to a trip.
 *
 * WHAT THIS REPLACES. Every add affordance on the trip page used to drop
 * straight into a blank form: pick a type from a dropdown, type a title, guess a
 * time. Meanwhile Steward — who knows the trip, can pull a real nightly rate and
 * a photo, and will argue you out of a 7pm dinner on the day of a 9pm flight —
 * was reachable only from a separate button, and only if you already knew you
 * wanted her. The traveler who taps "Before you fly · get to SEA" is exactly the
 * traveler who should be offered a car and a shuttle, and they were the one
 * person the feature never spoke to.
 *
 * So: pick WHAT, then pick HOW. Both paths lead to the same itinerary row —
 * Steward's card and the manual form call the identical
 * POST /trips/:id/itinerary-items. Nothing here is a dead end, and nothing here
 * forces a conversation on someone who just wants to type "Dinner, 7pm".
 */
import { useEffect, useState } from "react";
import {
  Bus,
  Car,
  Compass,
  Home,
  Hotel,
  Landmark,
  PenLine,
  Sparkles,
  Star,
  StickyNote,
  TrainFront,
  Utensils,
  Ticket,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ItineraryItemType, StewardScope } from "@/lib/types";
import {
  ADD_CHOICES,
  buildFreeScope,
  buildScope,
  buildSeedMessage,
  contextSubline,
  findChoice,
  formatSlotDay,
  type AddChoice,
  type AddContext,
} from "./addToItinerary-utils";
import type { PlaceSuggestion } from "./tripPlaces";

const ICONS: Record<AddChoice["icon"], LucideIcon> = {
  Hotel,
  Home,
  Utensils,
  Ticket,
  Landmark,
  Compass,
  Car,
  TrainFront,
  Bus,
  Star,
  StickyNote,
};

export interface AddRequest {
  date: string | null;
  city: string | null;
  itemType: ItineraryItemType;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where on the trip the traveler tapped. */
  context: AddContext;
  /** Trip days, so the slot's day can be retargeted without leaving. */
  dayDates: string[];
  /**
   * Everywhere the trip goes (see tripPlaces). The Where field is a SUGGESTION
   * list on top of free text, never a whitelist — the slot's city is a starting
   * point, not a cage.
   */
  places?: PlaceSuggestion[];
  /**
   * Pre-select a tile (an `AddChoice.key`). Set when the traveler arrived from
   * somewhere that already knows WHAT is missing — the completeness rail's
   * "plan this" action — so they land one tap from an answer instead of being
   * asked a question they already answered.
   */
  defaultChoiceKey?: string | null;
  /**
   * Hand off to Steward with the request already phrased. OPTIONAL: without it
   * the sheet degrades to a manual-only picker rather than showing a button
   * that does nothing.
   */
  onAskSteward?: (seed: { message: string; scope: StewardScope }) => void;
  /** Hand off to the manual form, prefilled. */
  onEnterManually: (request: AddRequest) => void;
}

export default function AddToItinerarySheet({
  open,
  onOpenChange,
  context,
  dayDates,
  places = [],
  defaultChoiceKey = null,
  onAskSteward,
  onEnterManually,
}: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [day, setDay] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [freeText, setFreeText] = useState("");

  // Re-arm on every open: a sheet that remembers last time's choice is a sheet
  // that adds the wrong thing to the wrong day when you're moving quickly.
  useEffect(() => {
    if (!open) return;
    setSelected(defaultChoiceKey);
    setFreeText("");
    setDay(context.date ?? dayDates[0] ?? "");
    setCity(context.city ?? "");
  }, [open, context.date, context.city, dayDates, defaultChoiceKey]);

  /*
   * The context as it stands right now — the day AND place pickers move it.
   *
   * `position` is dropped the moment the traveler retargets the city. "After
   * you land at ANC" describes the SLOT, not the request; carrying it onto a
   * hotel in Anchorage that they deliberately moved off the Vancouver leg would
   * feed Steward a confident, specific, wrong premise.
   */
  const movedCity = (city.trim() || null) !== (context.city ?? null);
  const liveContext: AddContext = {
    ...context,
    date: day || context.date || null,
    city: city.trim() || null,
    position: movedCity ? null : context.position,
  };
  const choice = findChoice(selected);
  const subline = contextSubline(liveContext);
  const steward = !!onAskSteward;

  const askSteward = (c: AddChoice) => {
    if (!onAskSteward) return enterManually(c);
    onAskSteward({
      message: buildSeedMessage(c, liveContext),
      scope: buildScope(c, liveContext),
    });
    onOpenChange(false);
  };

  const enterManually = (c: AddChoice) => {
    onEnterManually({
      date: liveContext.date ?? null,
      city: liveContext.city ?? null,
      itemType: c.itemType,
    });
    onOpenChange(false);
  };

  const submitFreeText = () => {
    const text = freeText.trim();
    if (!text || !onAskSteward) return;
    onAskSteward({ message: text, scope: buildFreeScope(liveContext) });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* The shared DialogContent has no height cap, and eleven tiles plus two
          actions is taller than a small phone. Without this the footer buttons
          sit off-screen with nothing to scroll — capped and scrollable HERE
          rather than in the primitive, so no other dialog changes behaviour. */}
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add to your trip</DialogTitle>
          <DialogDescription>
            {subline ?? "Pick what you're adding — Steward can find it, or enter it yourself."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Say it in your own words. Above the grid because a traveler who
              already knows what they want should never have to find their thing
              in a taxonomy first. */}
          {steward && (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              submitFreeText();
            }}
          >
            <div className="relative flex-1">
              <Sparkles
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[hsl(var(--primary))]"
                aria-hidden="true"
              />
              <input
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Tell Steward what you need — “a car for the drive to Seward”"
                aria-label="Tell Steward what you need"
                className="w-full rounded-lg border border-border bg-card py-2 pl-9 pr-3 text-sm outline-none placeholder:text-muted-foreground focus:border-[hsl(var(--primary))]/50"
              />
            </div>
            <button
              type="submit"
              disabled={freeText.trim().length === 0}
              className="inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-lg bg-[hsl(var(--foreground))] px-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40 motion-reduce:transition-none"
            >
              Ask
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
          )}

          {steward && (
            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-border" />
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                or pick one
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
          )}

          {/* What */}
          <div
            role="radiogroup"
            aria-label="What are you adding?"
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4"
          >
            {ADD_CHOICES.map((c) => {
              const Icon = ICONS[c.icon];
              const active = selected === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setSelected(c.key)}
                  onDoubleClick={() => (c.category ? askSteward(c) : enterManually(c))}
                  className={
                    "flex flex-col items-start gap-1 rounded-lg border p-2.5 text-left transition-colors motion-reduce:transition-none " +
                    (active
                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.06] ring-1 ring-[hsl(var(--primary))]/30"
                      : "border-border bg-card hover:border-[hsl(var(--primary))]/40 hover:bg-[hsl(var(--primary))]/[0.03]")
                  }
                >
                  <Icon
                    className="h-4 w-4 text-[hsl(var(--primary))]"
                    aria-hidden="true"
                  />
                  <span className="text-xs font-semibold text-foreground">{c.label}</span>
                  <span className="hidden text-[11px] leading-tight text-muted-foreground sm:block">
                    {c.hint}
                  </span>
                </button>
              );
            })}
          </div>

          {/* When and WHERE — both always visible, so retargeting never means
              backing out. The place field is the fix for a cruise trip offering
              only the embarkation port: the disembark city is a suggestion
              away, and anywhere else is one word of typing. */}
          <div className="grid gap-3 sm:grid-cols-2">
            {dayDates.length > 0 && (
              <div className="space-y-1">
                <label htmlFor="add-day" className="text-xs text-muted-foreground">
                  Day
                </label>
                <Select value={day} onValueChange={setDay}>
                  <SelectTrigger id="add-day" className="h-9 text-xs">
                    <SelectValue placeholder="Pick a day" />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    {dayDates.map((d) => (
                      <SelectItem key={d} value={d}>
                        {formatSlotDay(d) ?? d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="add-city" className="text-xs text-muted-foreground">
                Where
              </label>
              <input
                id="add-city"
                list="add-city-options"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Anchorage"
                autoComplete="off"
                className="h-9 w-full rounded-md border border-border bg-card px-3 text-xs outline-none placeholder:text-muted-foreground focus:border-[hsl(var(--primary))]/50"
              />
              <datalist id="add-city-options">
                {places.map((p) => (
                  <option key={p.city} value={p.city}>
                    {p.reason}
                  </option>
                ))}
              </datalist>
            </div>
          </div>

          {/* One tap for the places this trip actually touches. A datalist alone
              hides them behind a caret most people never press. */}
          {places.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {places.slice(0, 5).map((p) => {
                const active = p.city.trim().toLowerCase() === city.trim().toLowerCase();
                return (
                  <button
                    key={p.city}
                    type="button"
                    onClick={() => setCity(p.city)}
                    title={p.reason}
                    className={
                      "rounded-full border px-2.5 py-1 text-[11px] transition-colors motion-reduce:transition-none " +
                      (active
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/[0.08] font-medium text-foreground"
                        : "border-border text-muted-foreground hover:border-[hsl(var(--primary))]/40 hover:text-foreground")
                    }
                  >
                    {p.city}
                  </button>
                );
              })}
            </div>
          )}

          {/* How. Both actions stay on screen and both stay disabled until the
              traveler has picked a thing, so the two paths read as equals
              instead of one being the default and the other a fallback. */}
          <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row">
            {steward && (
              <button
                type="button"
                disabled={!choice || !choice.category}
                onClick={() => choice && askSteward(choice)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[hsl(var(--foreground))] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40 motion-reduce:transition-none"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {choice?.category
                  ? `Find ${article(choice.label)} with Steward`
                  : "Find it with Steward"}
              </button>
            )}
            <button
              type="button"
              disabled={!choice}
              onClick={() => choice && enterManually(choice)}
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:opacity-40 motion-reduce:transition-none"
            >
              <PenLine className="h-4 w-4" aria-hidden="true" />
              Enter it myself
            </button>
          </div>

          {!choice && steward && (
            <p className="text-center text-xs text-muted-foreground">
              Steward researches it, prices what can be priced, and hands you a card
              to add. Nothing is booked without you.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** "a hotel" / "an attraction" — a small courtesy that makes the button read. */
export function article(label: string): string {
  const l = label.toLowerCase();
  return /^[aeiou]/.test(l) ? `an ${l}` : `a ${l}`;
}
